import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  createExternalDeployment,
  listExternalDeployments,
  releaseExternalDeployment,
  requestExternalDeployment,
} from "../services/externalDeploymentService";
import { getApiErrorMessage, showApiErrorToast } from "../utils/apiError";

const INITIAL_FORM = {
  projectId: "",
  title: "",
  scope: "",
  engagementModel: "fixed_scope",
  requestNotes: "",
};

const OPEN_STATUSES = new Set([
  "draft",
  "ready_for_review",
  "requested",
  "released",
  "in_execution",
  "proof_submitted",
  "verified",
  "invoice_pending",
]);

function statusTone(status) {
  const key = String(status || "").trim().toLowerCase();
  if (["verified", "invoice_pending"].includes(key)) return "bg-emerald-100 text-emerald-800";
  if (["released", "in_execution", "proof_submitted"].includes(key)) return "bg-blue-100 text-blue-800";
  if (["requested", "ready_for_review"].includes(key)) return "bg-amber-100 text-amber-800";
  if (["cancelled"].includes(key)) return "bg-rose-100 text-rose-800";
  return "bg-slate-100 text-slate-700";
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("nl-NL");
}

export default function ExternalDeploymentsPage() {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(INITIAL_FORM);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await listExternalDeployments();
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      setError(getApiErrorMessage(err, "Externe inzet laden mislukt"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function handleCreate(event) {
    event.preventDefault();
    try {
      setSubmitting(true);
      setError("");
      await createExternalDeployment({
        ...form,
      });
      setForm(INITIAL_FORM);
      setShowForm(false);
      await load();
    } catch (err) {
      showApiErrorToast(err, { fallbackMessage: "Externe inzet aanmaken mislukt" });
    } finally {
      setSubmitting(false);
    }
  }

  async function handleQuickRequest(item) {
    try {
      await requestExternalDeployment(item._id, {});
      await load();
    } catch (err) {
      showApiErrorToast(err, { fallbackMessage: "Uitvraag starten mislukt" });
    }
  }

  async function handleQuickRelease(item) {
    try {
      await releaseExternalDeployment(item._id, {});
      await load();
    } catch (err) {
      showApiErrorToast(err, { fallbackMessage: "Vrijgave mislukt" });
    }
  }

  const openCount = items.filter((item) => OPEN_STATUSES.has(String(item?.status || ""))).length;

  return (
    <div className="space-y-4">
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">Logboek24</p>
            <h1 className="text-2xl font-semibold text-slate-900">Externe inzet</h1>
            <p className="text-sm text-slate-600">
              Aanvraag, concept-order, vrijgave, uitvoering en bewijs in 1 flow.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
              Open: {openCount}
            </span>
            <button
              type="button"
              onClick={() => setShowForm((prev) => !prev)}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white"
            >
              {showForm ? "Formulier sluiten" : "Nieuwe externe inzet"}
            </button>
          </div>
        </div>
      </section>

      {showForm ? (
        <form onSubmit={handleCreate} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="grid gap-3 md:grid-cols-2">
            <input
              value={form.projectId}
              onChange={(e) => setForm((prev) => ({ ...prev, projectId: e.target.value }))}
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
              placeholder="Project ID"
              required
            />
            <select
              value={form.engagementModel}
              onChange={(e) => setForm((prev) => ({ ...prev, engagementModel: e.target.value }))}
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
            >
              <option value="fixed_scope">fixed_scope</option>
              <option value="quantity_based">quantity_based</option>
              <option value="time_based">time_based</option>
            </select>
            <input
              value={form.title}
              onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm md:col-span-2"
              placeholder="Titel"
              required
            />
            <textarea
              value={form.scope}
              onChange={(e) => setForm((prev) => ({ ...prev, scope: e.target.value }))}
              className="min-h-[90px] rounded-lg border border-slate-200 px-3 py-2 text-sm md:col-span-2"
              placeholder="Scope van de opdracht"
              required
            />
            <input
              value={form.requestNotes}
              onChange={(e) => setForm((prev) => ({ ...prev, requestNotes: e.target.value }))}
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm md:col-span-2"
              placeholder="Interne notitie (optioneel)"
            />
          </div>
          <div className="mt-3 flex justify-end">
            <button
              type="submit"
              disabled={submitting}
              className="rounded-lg bg-emerald-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
            >
              {submitting ? "Opslaan..." : "Aanmaken + concept-order"}
            </button>
          </div>
        </form>
      ) : null}

      {error ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>
      ) : null}

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2">Inzet</th>
                <th className="px-4 py-2">Model</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Project</th>
                <th className="px-4 py-2">Order</th>
                <th className="px-4 py-2">Acties</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {!loading && items.length === 0 ? (
                <tr>
                  <td className="px-4 py-6 text-slate-500" colSpan={6}>
                    Nog geen externe inzet gevonden.
                  </td>
                </tr>
              ) : null}
              {items.map((item) => (
                <tr key={item._id} className="hover:bg-slate-50">
                  <td className="px-4 py-2">
                    <Link to={`/pl/externe-inzet/${item._id}`} className="font-medium text-slate-900 hover:underline">
                      {item.title || "Externe inzet"}
                    </Link>
                    <p className="text-xs text-slate-500">{formatDate(item.createdAt)}</p>
                  </td>
                  <td className="px-4 py-2 text-slate-700">{item.engagementModel || "-"}</td>
                  <td className="px-4 py-2">
                    <span className={`rounded-full px-2 py-1 text-xs font-medium ${statusTone(item.status)}`}>
                      {item.status || "-"}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-slate-600">{String(item.projectId || "-")}</td>
                  <td className="px-4 py-2 text-slate-600">{String(item.procurementOrderId || "-")}</td>
                  <td className="px-4 py-2">
                    <div className="flex flex-wrap gap-2">
                      {item.status === "draft" || item.status === "ready_for_review" ? (
                        <button
                          type="button"
                          onClick={() => handleQuickRequest(item)}
                          className="rounded border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-800"
                        >
                          Uitvraag
                        </button>
                      ) : null}
                      {item.status === "requested" || item.status === "ready_for_review" || item.status === "draft" ? (
                        <button
                          type="button"
                          onClick={() => handleQuickRelease(item)}
                          className="rounded border border-blue-300 bg-blue-50 px-2 py-1 text-xs text-blue-800"
                        >
                          Vrijgeven
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
